import React, { useState, useEffect } from 'react';
import { 
  Page, 
  Card, 
  Text, 
  List, 
  Badge, 
  Divider, 
  Layout, 
  LegacyStack,
  Banner,
  Filters,
  ResourceList,
  ResourceItem,
  Pagination,
  Icon,
  TextStyle,
  Tooltip,
  Select
} from '@shopify/polaris';
import { NoteMajor, SearchMajor } from '@shopify/polaris-icons';
import { version } from '../utils/version';
import { changelogEntries, getVersionTypes, getVersionBadge, getLatestVersion } from '../data/changelogData';

const Changelog = () => {
  const [entries, setEntries] = useState(changelogEntries);
  const [filteredEntries, setFilteredEntries] = useState(changelogEntries);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [searchValue, setSearchValue] = useState('');
  const [appliedFilters, setAppliedFilters] = useState([]);
  const latestVersion = getLatestVersion();
  
  // Filter entries when search or filters change
  useEffect(() => {
    if (entries.length === 0) return;
    
    let filtered = [...entries];
    
    // Apply search
    if (searchValue) {
      const lowerSearch = searchValue.toLowerCase();
      filtered = filtered.filter(entry => {
        // Search in title, description, and changes
        const titleMatch = entry.title.toLowerCase().includes(lowerSearch);
        const descriptionMatch = entry.description.toLowerCase().includes(lowerSearch);
        const changesMatch = entry.changes.some(change => 
          change.toLowerCase().includes(lowerSearch)
        );
        const versionMatch = entry.version.includes(lowerSearch);
        
        return titleMatch || descriptionMatch || changesMatch || versionMatch;
      });
    }
    
    // Apply filters
    if (appliedFilters.length > 0) {
      appliedFilters.forEach(filter => {
        if (filter.key === 'type') {
          filtered = filtered.filter(entry => entry.type === filter.value);
        }
      });
    }
    
    setFilteredEntries(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchValue, appliedFilters, entries]);

  // Get current entries for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEntries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);
  
  // Handle filter removal and clear all
  const handleRemoveFilter = (key) => {
    setAppliedFilters(appliedFilters.filter(filter => filter.key !== key));
  };
  
  const handleClearAll = () => {
    setAppliedFilters([]);
    setSearchValue('');
  };
  
  // Handle filter selection
  const handleTypeFilterChange = (value) => {
    setAppliedFilters(current => {
      const newFilters = current.filter(filter => filter.key !== 'type');
      if (value) {
        newFilters.push({
          key: 'type',
          value: value,
          label: value.charAt(0).toUpperCase() + value.slice(1) + ' Releases'
        });
      }
      return newFilters;
    });
  };
  
  // Filter configuration
  const filterConfig = [
    {
      key: 'type',
      label: 'Release Type',
      filter: (
        <Select
          label="Release Type"
          labelHidden
          options={getVersionTypes()}
          onChange={handleTypeFilterChange}
          value={appliedFilters.find(filter => filter.key === 'type')?.value || ''}
        />
      ),
      shortcut: true
    }
  ];

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    
    try {
      const date = new Date(dateStr);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateStr);
      return 'Date error';
    }
  };

  return (
    <Page title="Changelog">
      <Layout>
        <Layout.Section>
          <Banner title={`Current Version: ${version}`} status="info">
            <p>This page tracks all updates and changes to Shopify Product Editor Pro.</p>
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Card.Section>
              <Filters
                queryValue={searchValue}
                filters={filterConfig}
                appliedFilters={appliedFilters.map(filter => ({
                  key: filter.key,
                  label: filter.label,
                  onRemove: () => handleRemoveFilter(filter.key)
                }))}
                onQueryChange={setSearchValue}
                onQueryClear={() => setSearchValue('')}
                onClearAll={handleClearAll}
                helpText="Search in version, title, description, or changes"
              />
              
              {filteredEntries.length > 0 ? (
                <>
                  <ResourceList
                    resourceName={{ singular: 'version', plural: 'versions' }}
                    items={currentItems}
                    renderItem={(item, index) => {
                      const { 
                        id, 
                        version: itemVersion, 
                        date, 
                        title, 
                        description, 
                        changes,
                        type
                      } = item;
                      
                      const isLatest = latestVersion && itemVersion === latestVersion.version;
                      
                      return (
                        <ResourceItem
                          id={id}
                          accessibilityLabel={`Version ${itemVersion}: ${title}`}
                          media={<Icon source={NoteMajor} color="base" />}
                        >
                          <LegacyStack vertical spacing="loose">
                            <LegacyStack distribution="equalSpacing" alignment="center">
                              <LegacyStack vertical spacing="extraTight">
                                <LegacyStack spacing="tight" alignment="center">
                                  <TextStyle variation="strong">Version {itemVersion}</TextStyle>
                                  <Badge status={getVersionBadge(type)}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                  </Badge>
                                  {isLatest && <Badge status="success">Latest</Badge>}
                                </LegacyStack>
                                <TextStyle variation="subdued">{formatDate(date)}</TextStyle>
                              </LegacyStack>
                              <TextStyle variation="strong">{title}</TextStyle>
                            </LegacyStack>
                            
                            <Text as="p">{description}</Text>
                            
                            <List type="bullet">
                              {changes.map((change, index) => (
                                <List.Item key={index}>{change}</List.Item>
                              ))}
                            </List>
                          </LegacyStack>
                          {index !== currentItems.length - 1 && <Divider />}
                        </ResourceItem>
                      );
                    }}
                  />
                  
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <Pagination
                      hasPrevious={currentPage > 1}
                      onPrevious={() => setCurrentPage(currentPage - 1)}
                      hasNext={currentPage < totalPages}
                      onNext={() => setCurrentPage(currentPage + 1)}
                    />
                  </div>
                </>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <TextStyle variation="subdued">No changelog entries found matching your criteria.</TextStyle>
                </div>
              )}
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Changelog;
